import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { PlusCircle, Filter as FilterIcon, FileText, Building2, ShieldAlert, Power, Pencil, Trash2 } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import DataTable from '../../../components/DataTable';
import Pagination from '../../../components/Pagination';
import DynamicFormModal from '../../../components/DynamicFormModal';
import FilterDrawer from '../../../components/FilterDrawer';
import AppliedFilters from '../../../components/AppliedFilters';
import ExportExcelButton from '../../../components/ExportExcelButton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useToast } from '../../../components/ui/toaster.jsx';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import {
  fetchIncoterms, createIncoterm, updateIncoterm, deleteIncoterm,
  toggleIncotermStatus, exportIncoterms,
} from './Services/incotermsService.js';
import { columnsExcel } from './utils/incotermsColumns.jsx';

const ITEMS_PER_PAGE = 10;
const SORTABLE = new Set(['codigo', 'nombre', 'version', 'estado', 'updatedAt']);

const formFields = [
  { id: 'codigo', label: 'Código', type: 'text', required: true, placeholder: 'FOB', maxLength: 3 },
  { id: 'nombre', label: 'Nombre', type: 'text', required: true, placeholder: 'Free On Board' },
  { id: 'version', label: 'Versión', type: 'text', required: false, placeholder: 'Incoterms 2020' },
  { id: 'descripcion', label: 'Descripción', type: 'textarea', required: false, rows: 3 },
  {
    id: 'estado', label: 'Estado', type: 'select', required: true, defaultValue: 'Activo',
    options: [{ value: 'Activo', label: 'Activo' }, { value: 'Inactivo', label: 'Inactivo' }],
  },
];

const formatDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('es-ES', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

export default function IncotermsPage() {
  const toast = useToast();
  const { tenant, isPlatformMode } = useAuth();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(ITEMS_PER_PAGE);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ codigo: 1 });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchAndSet = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await fetchIncoterms({ limit: itemsPerPage, offset: currentPage, query: filters, sort });
      setDisplayedData(r?.data || []);
      setTotalItems(r?.totalRecords || 0);
    } catch (e) {
      toast.error('Error al cargar Incoterms', e?.message);
    } finally { setIsLoading(false); }
  }, [currentPage, itemsPerPage, filters, sort, toast]);

  useEffect(() => { fetchAndSet(); }, [fetchAndSet]);

  const filterFields = useMemo(() => [
    { id: 'estado', label: 'Estado', type: 'select',
      options: [{ value: 'Activo', label: 'Activo' }, { value: 'Inactivo', label: 'Inactivo' }] },
    { id: 'codigo', label: 'Código contiene', type: 'text' },
    { id: 'nombre', label: 'Nombre contiene', type: 'text' },
  ], []);

  const dataColumns = useMemo(() => [
    { id: 'codigo', label: 'Código', sortable: true, width: '110px',
      render: (row) => <code className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-mono font-semibold">{row.codigo}</code> },
    { id: 'nombre', label: 'Nombre', sortable: true },
    { id: 'version', label: 'Versión', sortable: true, width: '160px',
      render: (row) => row.version || <span className="text-muted-foreground text-xs">—</span> },
    { id: 'descripcion', label: 'Descripción',
      render: (row) => <span className="text-sm text-muted-foreground line-clamp-2">{row.descripcion || '—'}</span> },
    { id: 'estado', label: 'Estado', sortable: true, width: '110px', type: 'status' },
    { id: 'updatedAt', label: 'Actualizado', sortable: true, width: '150px',
      render: (row) => <span className="text-xs text-muted-foreground">{formatDateTime(row.updatedAt)}</span> },
  ], []);

  const tableColumns = useMemo(() => [
    ...dataColumns,
    { id: 'actions', key: 'actions', label: 'Acciones', actions: [
      { key: 'edit', icon: 'Edit', tooltip: 'Editar', className: 'hover:bg-foreground hover:text-background' },
      { key: 'toggle', icon: 'Power', tooltip: (r) => (r.estado === 'Activo' ? 'Inactivar' : 'Activar'), className: 'hover:bg-amber-500 hover:text-white' },
      { key: 'delete', icon: 'Trash', tooltip: 'Eliminar', className: 'hover:bg-destructive hover:text-destructive-foreground' },
    ]},
  ], [dataColumns]);

  const handleSort = useCallback((colId) => {
    if (!SORTABLE.has(colId)) return;
    setSort((prev) => {
      const cur = prev[colId];
      if (cur === 1 || cur === 'asc') return { [colId]: -1 };
      if (cur === -1 || cur === 'desc') return { codigo: 1 };
      return { [colId]: 1 };
    });
    setCurrentPage(1);
  }, []);

  const openCreate = () => { setCurrentItem(null); setIsModalOpen(true); };
  const openEdit = (row) => { setCurrentItem(row); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setCurrentItem(null); };

  const handleSave = async (fd) => {
    const payload = { ...fd, codigo: (fd.codigo || '').toUpperCase() };
    const r = currentItem
      ? await updateIncoterm(currentItem._id, payload)
      : await createIncoterm(payload);
    if (r?.success) {
      toast.success(currentItem ? 'Incoterm actualizado' : 'Incoterm creado');
      closeModal(); fetchAndSet();
    } else {
      toast.error('No se pudo guardar', r?.message);
    }
  };

  const handleDeleteConfirmation = useCallback((r) => { setItemToDelete(r); setIsDeleteDialogOpen(true); }, []);
  const handleDelete = async () => {
    if (!itemToDelete) return;
    const r = await deleteIncoterm(itemToDelete._id);
    if (r?.success) {
      toast.success('Incoterm eliminado', `${itemToDelete.codigo} · ${itemToDelete.nombre}`);
      if (displayedData.length === 1 && currentPage > 1) setCurrentPage((p) => p - 1);
      else fetchAndSet();
    } else {
      toast.error('No se pudo eliminar', r?.message);
    }
    setIsDeleteDialogOpen(false); setItemToDelete(null);
  };

  const handleToggleStatus = useCallback(async (row) => {
    const r = await toggleIncotermStatus(row._id);
    if (r?.success) {
      const isActive = r.data?.estado === 'Activo';
      toast.success(isActive ? 'Incoterm activado' : 'Incoterm inactivado', `${row.codigo} · ${row.nombre}`);
      fetchAndSet();
    } else {
      toast.error('No se pudo cambiar el estado', r?.message);
    }
  }, [fetchAndSet, toast]);

  const handleTableAction = useCallback((type, row) => {
    if (type === 'edit') return openEdit(row);
    if (type === 'delete') return handleDeleteConfirmation(row);
    if (type === 'toggle') return handleToggleStatus(row);
  }, [handleDeleteConfirmation, handleToggleStatus]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg"><FileText className="h-8 w-8 text-primary" /></div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-2xl">Parametrización de Incoterms</CardTitle>
                {tenant ? (
                  <Badge variant="secondary" className="flex items-center gap-1 font-normal">
                    <Building2 className="h-3 w-3" />{tenant.nombre || tenant.slug}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="flex items-center gap-1 font-normal text-amber-600 border-amber-300">
                    <ShieldAlert className="h-3 w-3" />Modo plataforma
                  </Badge>
                )}
              </div>
              <CardDescription>
                {tenant ? `Catálogo de Incoterms del operador ${tenant.nombre || tenant.slug}.` : 'Catálogo global de Incoterms (Incoterms 2020).'}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <ExportExcelButton service={exportIncoterms} columns={columnsExcel}
              fileName={`incoterms_${new Date().toISOString().slice(0, 10)}`} filters={filters} />
            <Button variant="outline" onClick={() => setIsDrawerOpen(true)}>
              <FilterIcon className="mr-2 h-4 w-4" /> Filtros
            </Button>
            <Button onClick={openCreate}><PlusCircle className="mr-2 h-4 w-4" /> Crear Incoterm</Button>
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters filters={filters} fields={filterFields}
            onRemoveFilter={(k) => setFilters((p) => { const n = { ...p }; delete n[k]; return n; })}
            onClear={() => { setFilters({}); setCurrentPage(1); }} />
          <DataTable columns={tableColumns} data={displayedData} isLoading={isLoading}
            onAction={handleTableAction} sort={sort} onSort={handleSort}
            emptyMessage="Sin Incoterms" emptyHint="No hay Incoterms que coincidan con los filtros." />
          {!isLoading && totalItems > 0 && (
            <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage} />
          )}
        </CardContent>
      </Card>
      <FilterDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}
        fields={filterFields} initialFilters={filters}
        onApply={(a) => { setFilters(a); setCurrentPage(1); }} />
      <DynamicFormModal isOpen={isModalOpen} onClose={closeModal} onSave={handleSave}
        fields={formFields} item={currentItem}
        title={currentItem ? 'Editar Incoterm' : 'Crear Incoterm'} maxWidth="max-w-xl" />
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar Incoterm?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete && <>Se eliminará <strong>{itemToDelete.nombre}</strong> (<code>{itemToDelete.codigo}</code>). Esta acción no se puede deshacer.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
